import http from '@/lib/request/svc/hr/http';
import getInstallationId from '@/lib/facades/get-installation-id';

interface RawJobAssignment {
  businessEntityId?: string | null;
  isPrimary?: boolean;
  active?: boolean;
}

interface RawJobAssignmentListEnvelope {
  success?: boolean;
  data?: { jobAssignments?: RawJobAssignment[] };
  error?: { message?: string };
}

/**
 * The business entity on the employee's primary active assignment, null when
 * they hold no assignment or the entity is unset. proto3-JSON omits empty
 * arrays and false booleans, so the list is read by shape. A refusal throws
 * with svc-hr's message, whatever its status.
 */
export async function getEmployeeBusinessEntityId(
  employeeId: string | number
): Promise<string | null> {
  const tenantId = getInstallationId();
  let response: unknown;

  try {
    ({ data: response } = await http.get(
      `/tenants/${tenantId}/employees/${employeeId}/job-assignments`
    ));
  } catch (e) {
    const message = (
      (e as { response?: { data?: unknown } } | undefined)?.response?.data as
        | RawJobAssignmentListEnvelope
        | undefined
    )?.error?.message;

    if (!message) {
      throw e;
    }

    throw new Error(message);
  }

  const envelope = response as RawJobAssignmentListEnvelope | undefined;

  if (envelope?.success !== true) {
    throw new Error(
      envelope?.error?.message || 'get employee job assignments failed'
    );
  }

  const assignments = Array.isArray(envelope.data?.jobAssignments)
    ? envelope.data.jobAssignments
    : [];
  const primary = assignments.find(
    entry => entry?.isPrimary === true && entry.active === true
  );

  return typeof primary?.businessEntityId === 'string'
    ? primary.businessEntityId
    : null;
}

export default getEmployeeBusinessEntityId;
